'use client';

import { createContext, ReactNode, useState, useEffect } from 'react';
import { setInitialLanguage } from '../client-translation';

export type LanguageCode = string;

const defaultLanguage = process.env.NEXT_PUBLIC_LANGUAGE?.toLowerCase() || 'de';

export const ServerLanguageContext =
  createContext<LanguageCode>(defaultLanguage);

type ServerLanguageProviderProps = {
  value: LanguageCode;
  children: ReactNode;
};

let setLanguageOverride: ((lang: LanguageCode) => void) | null = null;

/**
 * Immediately updates the language context from client-side code,
 * without waiting for a server re-render. Triggers the same sync
 * chain as a server-driven update: setInitialLanguage → context value
 * change → useClientTranslation effect → i18n.changeLanguage().
 */
export function setClientLanguage(lang: LanguageCode) {
  setLanguageOverride?.(lang);
}

export function ServerLanguageProvider({
  value,
  children,
}: ServerLanguageProviderProps) {
  const [language, setLanguage] = useState(value);

  useEffect(() => {
    setLanguageOverride = setLanguage;

    return () => {
      setLanguageOverride = null;
    };
  }, []);

  // Sync with the server value when it changes (e.g. after router.refresh).
  useEffect(() => {
    setLanguage(value);
  }, [value]);

  // Set the language before any child component calls initI18n().
  // React renders top-down, so this runs before useI18n() in descendants.
  setInitialLanguage(language);

  return (
    <ServerLanguageContext.Provider value={language}>
      {children}
    </ServerLanguageContext.Provider>
  );
}
